import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { observer } from 'mobx-react';
import Iframe from 'components/Iframe/Iframe';
import { Box, Flex } from 'components/Layout/Layout';
import { FormattedMessage, useIntl } from 'react-intl';
import VideoPlayer from 'components/VideoPlayer/VideoPlayer';
import VideoAnnotationWrapper from 'components/VideoPlayer/VideoAnnotationWrapper';
import Annotation from 'components/VideoPlayer/Annotation';
import TimeRange from 'components/VideoPlayer/TimeRange';
import P from 'components/P/P';
import Button from 'components/Button/Button';
import Icon from 'components/Icon/Icon';
import TextArea from 'components/TextArea/TextArea';
import {
  ANALYSE_BUILDING_BLOCKS,
  ANALYSE_TIME_RANGE,
  SURVEY_URL
} from '../../constants';
import { ROUTE_ANALYSE_INFO, ROUTE_ANALYSE_OVERVIEW } from 'RouteList';
import messages from './messages';
import Ahref from '../../components/Ahref/Ahref';
import { ROUTE_TIPS } from '../../RouteList';
import {
  QUERY_GET_ANALYSE,
  QUERY_GET_VIDEO_ANALYSE,
  updateAnnotation,
  addAnnotation,
  finishAnalyse,
  QUERY_GET_VIDEO_ANALYSES,
  finishSurvey,
  deleteAnnotation
} from '../../services/graphql/video-analyse-query';
import Analyse from './store/model/Analyse';
import VideoAnalyse from './store/model/VideoAnalyse';
import ListItem from '../../components/List/ListItem';
import List from '../../components/List/List';
import Survey from './store/model/Survey';
import H2 from '../../components/H2/H2';
import { useStores } from '../../store/useStores';
import { useEventListener } from '../../utils/useEventListener';
import { useQuery } from '@apollo/client';

/* @TODO shortcuts nog aanvullen in copy files + testen :-) */
const getDefaultAnnotations = intl =>
  Array(6)
    .fill()
    .map((v, i) => ({
      label: intl.formatMessage(messages[`defaultAnnotation${i + 1}Label`]),
      value: intl.formatMessage(messages[`defaultAnnotation${i + 1}`])
    }));

const formatSecondsTo = sec => {
  var hrs = Math.floor(sec / 3600);
  var min = Math.floor((sec - hrs * 3600) / 60);
  var seconds = sec - hrs * 3600 - min * 60;
  seconds = Math.round(Math.round(seconds * 100) / 100);

  var result = hrs;
  result += ':' + (min < 10 ? '0' + min : min) + ':';
  result += seconds < 10 ? '0' + seconds : seconds;
  return result;
};

const findItemById = (list, id) => {
  let result = null;
  list.forEach(data => {
    data.data.forEach(items => {
      const buildingBlock = items.items.find(item => item.id === id);
      if (buildingBlock) {
        result = buildingBlock;
      }
    });
  });
  return result;
};

const getTimeRanges = (videoDuration, analyseTimeRange) => {
  const timeRanges = [];
  if (videoDuration && videoDuration > analyseTimeRange) {
    const ranges = Math.floor(videoDuration / analyseTimeRange);
    for (let i = 0; i < ranges; i++) {
      timeRanges.push(Math.round(i * analyseTimeRange + analyseTimeRange));
    }
  }
  return timeRanges;
};

function Video({ analyseId, videoAnalyseId }) {
  const intl = useIntl();
  const { routing, uiState, authStore } = useStores();
  const { isImpersonator, user } = authStore;
  const currentUser = isImpersonator || user;

  // Data fetching (Apollo hooks)
  const {
    loading: loadingVideoAnalyse,
    error: errorVideoAnalyse,
    data: dataVideoAnalyse
  } = useQuery(QUERY_GET_VIDEO_ANALYSE, {
    variables: { videoAnalyseId },
    fetchPolicy: 'cache-and-network'
  });

  const {
    loading: loadingAnalyse,
    error: errorAnalyse,
    data: dataAnalyse
  } = useQuery(QUERY_GET_ANALYSE, {
    variables: { analyseId },
    fetchPolicy: 'cache-and-network'
  });

  // Instance fields -> refs
  const videoPlayerRef = useRef(null);
  const activeAnalyseRef = useRef(null);
  const currentSurveyRef = useRef(null);
  const currentTimeRef = useRef(0);
  const currentTextRef = useRef('');
  const nextPauseTimeRef = useRef(0);

  // Local UI state
  const [activeAnnotation, setActiveAnnotation] = useState(null);
  const [videoDuration, setVideoDuration] = useState(null);
  const [currentText, setCurrentText] = useState('');
  const [canSave, setCanSave] = useState(false);
  const [timeRanges, setTimeRanges] = useState(null);
  const [activeSurveyUrl, setActiveSurveyUrl] = useState(null);

  const showSurvey = useCallback((survey, force) => {
    currentSurveyRef.current = survey;
    const buildingBlockUrl = findItemById(
      ANALYSE_BUILDING_BLOCKS,
      survey.buildingBlockId
    );

    if (!buildingBlockUrl || !buildingBlockUrl.url) {
      // eslint-disable-next-line no-console
      console.warn(
        '[Video] No buildingBlockUrl found for',
        survey.buildingBlockId
      );
      return;
    }

    const urlId = buildingBlockUrl.url;

    let URL = `${SURVEY_URL}${urlId}`;
    if (force) URL = URL + `&t=${new Date().getTime()}`;

    setActiveSurveyUrl(prev => {
      if (prev !== URL || force) return URL;
      return prev;
    });
  }, []);

  // Ensure the first survey is shown as soon as we have enough data.
  // Previously this depended on onTimeUpdate()/onMetadata() timing and often never ran,
  // leaving activeSurveyUrl null and the iframe branch unrendered.
  useEffect(() => {
    if (activeSurveyUrl || !timeRanges) return;

    const activeAnalyse = activeAnalyseRef.current;
    if (!activeAnalyse) return;

    const currentIndex = (timeRanges || []).findIndex(
      tr => tr >= currentTimeRef.current
    );
    const safeIndex = currentIndex === -1 ? timeRanges.length : currentIndex;

    const survey = activeAnalyse.getSurveyToShow(
      currentUser.email,
      safeIndex,
      timeRanges.length === safeIndex
    );

    if (survey) {
      // eslint-disable-next-line no-console
      console.log('[Video] initial survey selected', survey);
      showSurvey(survey);
    }
  }, [activeSurveyUrl, currentUser.email, showSurvey, timeRanges]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getCurrentIndex = useCallback(() => {
    const ranges = timeRanges || [];
    const index = ranges.findIndex(tr => tr >= currentTimeRef.current);
    if (index === -1) return ranges.length;
    return index;
  }, [timeRanges]);

  const showBuildingBlockToSurvey = useCallback(
    nextSurvey => {
      const activeAnalyse = activeAnalyseRef.current;
      if (!activeAnalyse || !timeRanges) return;

      const currentIndex = getCurrentIndex();
      const survey = activeAnalyse.getSurveyToShow(
        currentUser.email,
        currentIndex,
        timeRanges.length === currentIndex,
        nextSurvey
      );
      if (survey) {
        showSurvey(survey, nextSurvey && nextSurvey.sapId === survey.sapId);
      }
    },
    [currentUser.email, getCurrentIndex, showSurvey, timeRanges]
  );

  const getNextPauseTime = useCallback(() => {
    const ranges = timeRanges || [];
    return ranges.find(tr => currentTimeRef.current < tr);
  }, [timeRanges]);

  const onTimeUpdate = useCallback(
    time => {
      currentTimeRef.current = Math.round(time * 100) / 100;

      if (
        activeAnnotation &&
        activeAnnotation.time !== currentTimeRef.current
      ) {
        if (currentText !== '') {
          setCurrentText('');
          setCanSave(false);
          setActiveAnnotation(null);
        } else {
          setActiveAnnotation(null);
        }
      }

      const player = videoPlayerRef.current;
      if (player) {
        if (player.isPlaying()) {
          if (nextPauseTimeRef.current <= time) {
            player.pause();
            player.player.currentTime(nextPauseTimeRef.current);
            nextPauseTimeRef.current = getNextPauseTime();
            if (player.isFullscreen()) player.exitFullscreen();
          }
        } else {
          nextPauseTimeRef.current = getNextPauseTime();
        }
      }

      showBuildingBlockToSurvey();
    },
    [activeAnnotation, currentText, getNextPauseTime, showBuildingBlockToSurvey]
  );

  const onPlay = useCallback(() => {
    nextPauseTimeRef.current = getNextPauseTime();
  }, [getNextPauseTime]);

  const onMetadata = useCallback(
    metadata => {
      if (videoDuration !== metadata.duration) {
        setVideoDuration(metadata.duration);
        const ranges = getTimeRanges(metadata.duration, ANALYSE_TIME_RANGE);
        setTimeRanges(ranges);
        // ensure pause time uses latest ranges
        nextPauseTimeRef.current = ranges.find(
          tr => currentTimeRef.current < tr
        );
        // Don't call showBuildingBlockToSurvey here - it can run before state is committed.
      }
    },
    [videoDuration]
  );

  const setActiveAnnotationAndSeek = useCallback(annotation => {
    currentTextRef.current = annotation.text;
    currentTimeRef.current = annotation.time;

    const player = videoPlayerRef.current;
    if (player) {
      if (player.isPlaying()) {
        player.pause();
      }
      player.player.currentTime(currentTimeRef.current);
    }

    setActiveAnnotation(annotation);
    setCurrentText(currentTextRef.current);
    setCanSave(false);
  }, []);

  const onTextChange = useCallback(
    text => {
      currentTextRef.current = text;
      let nextCanSave = false;

      if (text !== '') {
        if (activeAnnotation && activeAnnotation.text !== text) {
          nextCanSave = true;
        } else if (!activeAnnotation) {
          nextCanSave = true;
        }
      }

      if (currentText !== text) setCurrentText(text);
      if (canSave !== nextCanSave) setCanSave(nextCanSave);
    },
    [activeAnnotation, canSave, currentText]
  );

  const onTextBlur = useCallback(() => {
    if (activeAnnotation) {
      setCurrentText(currentTextRef.current);
    }
  }, [activeAnnotation]);

  const onAddUpdateAnnotation = useCallback(
    async text => {
      const activeAnalyse = activeAnalyseRef.current;
      if (!activeAnalyse) return;

      let currentAnnotation;
      const refetchQueries = [
        { query: QUERY_GET_ANALYSE, variables: { analyseId } }
      ];

      if (activeAnnotation) {
        const existing = activeAnalyse.annotations.find(
          a => a.id === activeAnnotation.id
        );
        currentAnnotation = await updateAnnotation(
          { id: existing.id, text },
          refetchQueries
        );
      } else {
        currentAnnotation = await addAnnotation(
          { analyseId: activeAnalyse.id, time: currentTimeRef.current, text },
          refetchQueries
        );
      }

      if (currentAnnotation) {
        setCurrentText(text);
        setCanSave(false);
        setActiveAnnotation(currentAnnotation);
      }
    },
    [activeAnnotation, analyseId]
  );

  const onDeleteAnnotationClick = useCallback(
    annotation => {
      uiState.createAlert({
        title: intl.formatMessage(messages.analyseVideoModalDeleteTitle),
        message: intl.formatMessage(messages.analyseVideoModalDeleteMessage),
        ok: intl.formatMessage(messages.analyseVideoModalDeleteOK),
        okHandler: async () => {
          await deleteAnnotation({ annotationId: annotation.id }, [
            { query: QUERY_GET_ANALYSE, variables: { analyseId } }
          ]);

          if (activeAnnotation && annotation.id === activeAnnotation.id) {
            currentTextRef.current = '';
            setCurrentText('');
            setCanSave(false);
            setActiveAnnotation(null);
          }
        },
        cancel: intl.formatMessage(messages.analyseVideoModalDeleteCancel)
      });
    },
    [activeAnnotation, analyseId, intl, uiState]
  );

  const onSurveyFinished = useCallback(
    async (survey, unique_identifier, score) => {
      if (!survey || survey.finished) return;
      survey.setFinished(unique_identifier, score);

      const activeAnalyse = activeAnalyseRef.current;
      if (!activeAnalyse) return;

      const response = await finishSurvey(
        {
          analyseId: activeAnalyse.id,
          buildBlockId: survey.buildingBlockId,
          index: survey.index,
          last: survey.last,
          finished: survey.finished,
          score: survey.score,
          sapId: survey.sapId
        },
        [
          {
            query: QUERY_GET_ANALYSE,
            variables: { analyseId: activeAnalyse.id }
          }
        ]
      );

      activeAnalyse.surveys.push(new Survey(response));
      showBuildingBlockToSurvey(response);
    },
    [showBuildingBlockToSurvey]
  );

  useEventListener(
    () => window,
    'message',
    e => {
      try {
        const data = e.data;
        if (data && data.unique_identifier) {
          onSurveyFinished(
            currentSurveyRef.current,
            data.unique_identifier,
            data.score
          );
        }
      } catch (er) {
        // eslint-disable-next-line no-console
        console.log('Error postMessage iFrame', er);
      }
    }
  );

  const onReadyClick = useCallback(async () => {
    const activeAnalyse = activeAnalyseRef.current;
    if (!activeAnalyse) return;

    activeAnalyse.finished = true;

    await finishAnalyse({ id: activeAnalyse.id, finished: true }, [
      {
        query: QUERY_GET_VIDEO_ANALYSES,
        variables: { personId: isImpersonator ? isImpersonator.id : user.id }
      }
    ]);

    routing.push(`${ROUTE_ANALYSE_OVERVIEW}/${videoAnalyseId}`);
  }, [isImpersonator, routing, user.id, videoAnalyseId]);

  const onGoToTipAndTricks = useCallback(() => {
    routing.push(`${ROUTE_TIPS}`);
  }, [routing]);

  const goBack = useCallback(() => {
    routing.push(`${ROUTE_ANALYSE_INFO}/${videoAnalyseId}/${analyseId}`);
  }, [analyseId, routing, videoAnalyseId]);

  if (loadingVideoAnalyse || loadingAnalyse) return <P>Bezig met laden...</P>;
  if (errorVideoAnalyse) return <P danger>{errorVideoAnalyse.message}</P>;
  if (errorAnalyse) return <P danger>{errorAnalyse.message}</P>;

  if (!dataVideoAnalyse?.getVideoAnalyse || !dataAnalyse?.getAnalyse) {
    return <P danger>{intl.formatMessage(messages.analiseErrorLoading)}</P>;
  }

  const activeVideoAnalyse = new VideoAnalyse(dataVideoAnalyse.getVideoAnalyse);
  const activeAnalyse = new Analyse(dataAnalyse.getAnalyse);
  activeAnalyseRef.current = activeAnalyse;

  const videoJsOptions = {
    playbackRates: [0.5, 1, 1.5, 2],
    sources: [{ src: activeVideoAnalyse.video.s3, type: 'video/mp4' }]
  };

  return (
    <Fragment>
      <Flex flexWrap="wrap">
        <Box width={1} mt={4} mb={2}>
          <H2 upper>
            <FormattedMessage {...messages.analyseVideoTitle} />
          </H2>
        </Box>
        <Box width={1} mt={1} mb={4} />

        <Flex width={[1, 1, 1 / 2]} pr={[0, 0, 2]} flexWrap="wrap">
          <Box width={1}>
            <VideoPlayer
              ref={videoPlayer => {
                videoPlayerRef.current = videoPlayer;
              }}
              {...videoJsOptions}
              onTimeUpdate={onTimeUpdate}
              onMetadata={onMetadata}
              onPlay={onPlay}
            />

            <VideoAnnotationWrapper>
              {activeAnalyse.annotations.map(annotation => (
                <Annotation
                  procent={(annotation.time / (videoDuration || 1)) * 100}
                  key={annotation.id}
                  selected={
                    activeAnnotation
                      ? activeAnnotation.time === annotation.time
                      : false
                  }
                  onClick={() => setActiveAnnotationAndSeek(annotation)}
                />
              ))}
              {timeRanges &&
                timeRanges.length > 0 &&
                timeRanges.map(tr => (
                  <TimeRange
                    key={tr}
                    procent={(tr / (videoDuration || 1)) * 100}
                  />
                ))}
            </VideoAnnotationWrapper>

            <Flex width={1}>
              {getDefaultAnnotations(intl).map((an, i) => (
                <Button
                  key={i}
                  secondary
                  inOneLine
                  onClick={() => onAddUpdateAnnotation(an.value)}
                >
                  {an.label}
                </Button>
              ))}
            </Flex>

            <Box width={1} my={2}>
              <TextArea
                textValue={currentText}
                onChange={onTextChange}
                onBlur={onTextBlur}
              />
            </Box>

            <Flex justifyContent="space-between">
              <Button
                small
                success
                disabled={!canSave}
                onClick={() => onAddUpdateAnnotation(currentTextRef.current)}
              >
                {activeAnnotation ? (
                  <FormattedMessage
                    {...messages.analiseVideoAnnotateButtonUpdate}
                  />
                ) : (
                  <FormattedMessage
                    {...messages.analiseVideoAnnotateButtonAdd}
                  />
                )}
              </Button>

              {activeAnnotation && (
                <Button
                  small
                  danger
                  onClick={() => onDeleteAnnotationClick(activeAnnotation)}
                >
                  <Flex alignItems="center" width={1}>
                    <Icon
                      id="delete"
                      fillColor={props => props.theme.colors.danger}
                    />
                  </Flex>
                </Button>
              )}
            </Flex>

            <List>
              {activeAnalyse.annotations
                .slice()
                .sort((obj1, obj2) => obj1.time - obj2.time)
                .map(annotation => (
                  <ListItem
                    key={annotation.id}
                    anotationList
                    metaData={
                      currentUser.id !== annotation.person.id
                        ? `${annotation.person.firstname} ${annotation.person.lastname}`
                        : ''
                    }
                    content={`${formatSecondsTo(annotation.time)} - ${
                      annotation.text
                    }`}
                  >
                    <Flex
                      width={[1, 3 / 7]}
                      justifyContent="flex-end"
                      mb={[3, 0]}
                    >
                      <Box width="auto" ml={2}>
                        <Button
                          small
                          primary
                          onClick={() => setActiveAnnotationAndSeek(annotation)}
                        >
                          <Flex alignItems="center">
                            <Icon id="edit" />
                          </Flex>
                        </Button>
                      </Box>
                      <Box width="auto" ml={2}>
                        <Button
                          small
                          danger
                          onClick={() => onDeleteAnnotationClick(annotation)}
                        >
                          <Flex alignItems="center">
                            <Icon
                              id="delete"
                              fillColor={props => props.theme.colors.danger}
                            />
                          </Flex>
                        </Button>
                      </Box>
                    </Flex>
                  </ListItem>
                ))}
            </List>

            <Box width={1} pt={3} mb={[3, 3, 0]}>
              <Button primary onClick={goBack} style={{ marginRight: 8 }}>
                <FormattedMessage {...messages.analyseBackButton} />
              </Button>
              <Button primary onClick={onReadyClick}>
                <FormattedMessage {...messages.analiseVideoReadyButton} />
              </Button>
            </Box>
          </Box>
        </Flex>

        <Flex width={[1, 1, 1 / 2]} pl={[0, 0, 2]} pt={[3, 0]}>
          <Box width={1}>
            {activeAnalyse && activeSurveyUrl && (
              <Fragment>
                <Iframe src={activeSurveyUrl} height={'100%'} width="100%" />

                <div style={{ display: 'flex' }}>
                  <div style={{ flex: '70%' }}>
                    {activeAnalyse.hasFinishedAllSurveys &&
                      activeAnalyse.surveys.map(survey => {
                        const buildingBlock = findItemById(
                          ANALYSE_BUILDING_BLOCKS,
                          survey.buildingBlockId
                        );

                        const isCurrent =
                          currentSurveyRef.current &&
                          currentSurveyRef.current.id === survey.id;

                        if (buildingBlock && survey.finished) {
                          return (
                            <Fragment
                              key={`${survey.buildingBlockId} ${survey.last}`}
                            >
                              <br />
                              <Ahref
                                active={isCurrent}
                                onClick={() => showSurvey(survey)}
                              >
                                {`${buildingBlock.label} ${
                                  survey.last ? '(Reflectie)' : ''
                                }`}
                              </Ahref>
                            </Fragment>
                          );
                        }
                        return null;
                      })}
                  </div>
                  {activeAnalyse.hasFinishedAllSurveys && (
                    <div style={{ flex: '30%' }}>
                      <br />
                      <Button primary full onClick={onGoToTipAndTricks}>
                        <FormattedMessage
                          {...messages.analiseVideoTipsAndTricksButton}
                        />
                      </Button>
                    </div>
                  )}
                </div>
              </Fragment>
            )}
          </Box>
        </Flex>
      </Flex>
    </Fragment>
  );
}

export default observer(Video);
